import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config.js';
import BIConfigOverview from './bi-config/BIConfigOverview.jsx';
import BIConfigEntriesTable from './bi-config/BIConfigEntriesTable.jsx';
import BIConfigEditorModal from './bi-config/BIConfigEditorModal.jsx';
import {
  buildDraftFromMetadata,
  countEnabledFeatures,
  countUpdatedToday,
  getDefaultOptions
} from './bi-config/utils.js';

const BIConfigManagement = ({ fetchWithAuth }) => {
  const [configs, setConfigs] = useState([]);
  const [modulesMetadata, setModulesMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [draft, setDraft] = useState(buildDraftFromMetadata(null));
  const [isSaving, setIsSaving] = useState(false);

  const selectedModuleMetadata = useMemo(() => {
    return modulesMetadata.find((moduleMetadata) => moduleMetadata.module === draft.module) || null;
  }, [draft.module, modulesMetadata]);

  const adminOnlyFlags = selectedModuleMetadata?.supported_flags?.admin_only || [];
  const supportedToggles = selectedModuleMetadata?.supported_toggles || [];

  const configMetrics = useMemo(() => ({
    totalConfigs: configs.length,
    totalModules: modulesMetadata.length,
    adminRestricted: configs.filter((config) => (config.options?.admin_only || []).length > 0).length,
    enabledFeatures: countEnabledFeatures(configs),
    updatedToday: countUpdatedToday(configs)
  }), [configs, modulesMetadata]);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [configsResponse, modulesResponse] = await Promise.all([
        fetchWithAuth(`${API_BASE_URL}/api/admin/bi-config`),
        fetchWithAuth(`${API_BASE_URL}/api/admin/bi-config/modules`)
      ]);

      if (!configsResponse.ok) {
        throw new Error('Failed to fetch BI config entries');
      }

      if (!modulesResponse.ok) {
        throw new Error('Failed to fetch BI config modules');
      }

      const [configsData, modulesData] = await Promise.all([
        configsResponse.json(),
        modulesResponse.json()
      ]);

      setConfigs(configsData);
      setModulesMetadata(modulesData);
      setPageError(null);
    } catch (loadError) {
      console.error('Error loading BI config management data:', loadError);
      setPageError('Failed to load BI config data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const syncAdvancedJson = (nextOptions) => {
    return JSON.stringify(nextOptions, null, 2);
  };

  const closeModal = useCallback(() => {
    setEditingConfig(null);
    setDraft(buildDraftFromMetadata(modulesMetadata[0] || null));
    setFormError(null);
    setIsModalOpen(false);
  }, [modulesMetadata]);

  const openCreateModal = useCallback(() => {
    const firstModule = modulesMetadata[0] || null;
    setEditingConfig(null);
    setDraft(buildDraftFromMetadata(firstModule));
    setFormError(null);
    setIsModalOpen(true);
  }, [modulesMetadata]);

  const openEditModal = useCallback((config) => {
    const moduleMetadata = modulesMetadata.find((module) => module.module === config.module) || null;
    setEditingConfig(config);
    setDraft(buildDraftFromMetadata(moduleMetadata, config));
    setFormError(null);
    setIsModalOpen(true);
  }, [modulesMetadata]);

  const updateDraftOptions = useCallback((updater) => {
    setDraft((currentDraft) => {
      const nextOptions = typeof updater === 'function'
        ? updater(currentDraft.options)
        : updater;

      return {
        ...currentDraft,
        options: nextOptions,
        advancedOptionsText: syncAdvancedJson(nextOptions)
      };
    });
    setFormError(null);
  }, []);

  const handleModuleChange = useCallback((event) => {
    const nextModule = event.target.value;
    const nextMetadata = modulesMetadata.find((moduleMetadata) => moduleMetadata.module === nextModule) || null;
    const nextOptions = editingConfig ? draft.options : getDefaultOptions(nextMetadata);

    setDraft((currentDraft) => ({
      ...currentDraft,
      module: nextModule,
      options: nextOptions,
      advancedOptionsText: syncAdvancedJson(nextOptions)
    }));
    setFormError(null);
  }, [draft.options, editingConfig, modulesMetadata]);

  const handleDiscriminantChange = useCallback((event) => {
    const { value } = event.target;
    setDraft((currentDraft) => ({
      ...currentDraft,
      discriminant_id: value
    }));
  }, []);

  const handleAdminOnlyFlagToggle = useCallback((flagValue) => {
    updateDraftOptions((currentOptions) => {
      const currentFlags = currentOptions?.admin_only || [];
      const hasFlag = currentFlags.includes(flagValue);
      const nextFlags = hasFlag
        ? currentFlags.filter((flag) => flag !== flagValue)
        : [...currentFlags, flagValue];

      return {
        ...currentOptions,
        admin_only: nextFlags
      };
    });
  }, [updateDraftOptions]);

  const handleBooleanToggleChange = useCallback((toggleKey) => {
    updateDraftOptions((currentOptions) => ({
      ...currentOptions,
      [toggleKey]: !currentOptions?.[toggleKey]
    }));
  }, [updateDraftOptions]);

  const handleAdvancedModeToggle = useCallback(() => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      advancedMode: !currentDraft.advancedMode,
      advancedOptionsText: syncAdvancedJson(currentDraft.options)
    }));
  }, []);

  const handleAdvancedJsonChange = useCallback((event) => {
    const nextJson = event.target.value;

    setDraft((currentDraft) => ({
      ...currentDraft,
      advancedOptionsText: nextJson
    }));
    setFormError(null);

    try {
      const parsedOptions = JSON.parse(nextJson);
      setDraft((currentDraft) => ({
        ...currentDraft,
        options: parsedOptions,
        advancedOptionsText: nextJson
      }));
    } catch (error) {}
  }, []);

  const handleSave = useCallback(async () => {
    let optionsToSave = draft.options;

    if (draft.advancedMode) {
      try {
        optionsToSave = JSON.parse(draft.advancedOptionsText || '{}');
      } catch (parseError) {
        setFormError('Options JSON must be valid before saving.');
        return;
      }
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const response = await fetchWithAuth(
        editingConfig
          ? `${API_BASE_URL}/api/admin/bi-config/${editingConfig.id}`
          : `${API_BASE_URL}/api/admin/bi-config`,
        {
          method: editingConfig ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            module: draft.module,
            discriminant_id: draft.discriminant_id,
            options: optionsToSave
          })
        }
      );

      if (!response.ok) {
        let message = 'Failed to save BI config entry.';

        try {
          const errorBody = await response.json();
          message = errorBody.message || message;
        } catch (error) {}

        throw new Error(message);
      }

      await loadData();
      closeModal();
    } catch (saveError) {
      console.error('Error saving BI config entry:', saveError);
      setFormError(saveError.message || 'Failed to save BI config entry.');
    } finally {
      setIsSaving(false);
    }
  }, [closeModal, draft, editingConfig, fetchWithAuth, loadData]);

  const handleDelete = useCallback(async (config) => {
    const confirmed = window.confirm(`Delete BI config "${config.module}" for discriminant "${config.discriminant_id}"?`);

    if (!confirmed) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/bi-config/${config.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        let message = 'Failed to delete BI config entry.';

        try {
          const errorBody = await response.json();
          message = errorBody.message || message;
        } catch (error) {}

        throw new Error(message);
      }

      await loadData();
      setPageError(null);
    } catch (deleteError) {
      console.error('Error deleting BI config entry:', deleteError);
      setPageError(deleteError.message || 'Failed to delete BI config entry.');
    }
  }, [fetchWithAuth, loadData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pr-1">
      <div className="min-h-full flex flex-col gap-6 pb-6">
        <BIConfigOverview
          configMetrics={configMetrics}
          onCreate={openCreateModal}
        />

        {pageError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-200">
            {pageError}
          </div>
        )}

        <BIConfigEntriesTable
          configs={configs}
          modulesMetadata={modulesMetadata}
          onEdit={openEditModal}
          onDelete={handleDelete}
        />

        <BIConfigEditorModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSave={handleSave}
          isSaving={isSaving}
          editingConfig={editingConfig}
          modulesMetadata={modulesMetadata}
          selectedModuleMetadata={selectedModuleMetadata}
          draft={draft}
          formError={formError}
          adminOnlyFlags={adminOnlyFlags}
          supportedToggles={supportedToggles}
          onModuleChange={handleModuleChange}
          onDiscriminantChange={handleDiscriminantChange}
          onAdminOnlyFlagToggle={handleAdminOnlyFlagToggle}
          onBooleanToggleChange={handleBooleanToggleChange}
          onAdvancedModeToggle={handleAdvancedModeToggle}
          onAdvancedJsonChange={handleAdvancedJsonChange}
        />
      </div>
    </div>
  );
};

export default BIConfigManagement;
